import { useEffect, useState } from "react";
import { http, API } from "@/lib/api";
import { toast } from "sonner";
import { Clapperboard, Instagram, GalleryHorizontalEnd, Loader2, Copy, Trash2, Sparkles, ImageIcon } from "lucide-react";

const TYPES = [
  { id: "tiktok", label: "Script TikTok / Reels", icon: Clapperboard },
  { id: "instagram", label: "Légende Instagram", icon: Instagram },
  { id: "carousel", label: "Carrousel (7 slides)", icon: GalleryHorizontalEnd },
];

const typeLabel = (id) => TYPES.find((t) => t.id === id)?.label || id;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const copyText = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copié dans le presse-papiers");
};

const VisualBlock = ({ item, imgUrl, imgLoadingId, onGenImage }) =>
  item.has_image ? (
    <div className="mt-4">
      <img src={imgUrl(item.id)} alt="Visuel généré" className="rounded-2xl border border-slate-200 w-full max-w-sm" data-testid={`studio-visual-${item.id}`} />
      <a href={imgUrl(item.id)} download={`beehive-visuel-${item.id}.png`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-800">
        <ImageIcon size={13} strokeWidth={1.75} /> Télécharger le visuel
      </a>
    </div>
  ) : (
    <button
      data-testid={`studio-gen-image-${item.id}`}
      onClick={() => onGenImage(item.id)}
      disabled={imgLoadingId !== null}
      className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 px-4 py-2 text-xs font-semibold hover:bg-slate-200 disabled:opacity-60"
    >
      {imgLoadingId === item.id ? <><Loader2 size={13} className="animate-spin" /> Création du visuel… (jusqu'à 1 min)</> : <><ImageIcon size={13} strokeWidth={1.75} /> Générer le visuel Instagram / LinkedIn</>}
    </button>
  );

const GeneratorForm = ({ formations, slug, setSlug, type, setType, loading, onGenerate }) => (
  <div className="card-soft p-6">
    <div className="flex items-center gap-2 mb-4">
      <Sparkles size={18} className="text-brand-600" strokeWidth={1.75} />
      <h2 className="font-display font-bold text-lg text-slate-900">Générer un contenu pour vos réseaux</h2>
    </div>
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Formation</label>
        <select
          data-testid="studio-formation-select"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring text-slate-700"
        >
          {formations.map((f) => <option key={f.slug} value={f.slug}>{f.title}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Type de contenu</label>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              data-testid={`studio-type-${t.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${type === t.id ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            >
              <t.icon size={13} strokeWidth={1.75} /> {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
    <button data-testid="studio-generate-btn" onClick={onGenerate} disabled={loading || !slug} className="btn-primary mt-5 text-sm disabled:opacity-70">
      {loading ? <><Loader2 size={16} className="animate-spin" /> Génération en cours… (10-30 s)</> : <>Générer <Sparkles size={15} strokeWidth={1.75} /></>}
    </button>
  </div>
);

const ResultCard = ({ result, visualProps }) => (
  <div className="card-soft p-6 mt-6" data-testid="studio-result">
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div>
        <span className="text-xs font-bold uppercase tracking-widest text-brand-700 bg-brand-50 rounded-full px-2.5 py-1">{typeLabel(result.content_type)}</span>
        <span className="text-xs text-slate-500 ml-2">{result.formation_title}</span>
      </div>
      <button data-testid="studio-copy-btn" onClick={() => copyText(result.content)} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-4 py-2 text-xs font-semibold hover:bg-slate-700">
        <Copy size={13} strokeWidth={1.75} /> Copier
      </button>
    </div>
    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{result.content}</p>
    <VisualBlock item={result} {...visualProps} />
  </div>
);

const HistoryList = ({ history, onRemove, visualProps }) => (
  <div className="mt-10">
    <h3 className="font-display font-bold text-slate-900 mb-4">Contenus générés précédemment</h3>
    <div className="grid gap-3">
      {history.map((h) => (
        <details key={h.id} className="card-soft p-5 group" data-testid={`studio-history-${h.id}`}>
          <summary className="cursor-pointer list-none flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[11px] font-bold uppercase tracking-widest text-brand-700 bg-brand-50 rounded-full px-2.5 py-1">{typeLabel(h.content_type)}</span>
              <span className="font-medium text-slate-900">{h.formation_title}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              {fmtDate(h.created_at)}
              <button onClick={(e) => { e.preventDefault(); copyText(h.content); }} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-600" title="Copier"><Copy size={14} strokeWidth={1.75} /></button>
              <button onClick={(e) => { e.preventDefault(); onRemove(h.id); }} className="p-1.5 rounded-full hover:bg-red-50 text-red-500" title="Supprimer"><Trash2 size={14} strokeWidth={1.75} /></button>
            </div>
          </summary>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line mt-4 pt-4 border-t border-slate-100">{h.content}</p>
          <VisualBlock item={h} {...visualProps} />
        </details>
      ))}
    </div>
  </div>
);

export const ContentStudio = () => {
  const [formations, setFormations] = useState([]);
  const [slug, setSlug] = useState("");
  const [type, setType] = useState("tiktok");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [imgLoadingId, setImgLoadingId] = useState(null);
  const [imgVersion, setImgVersion] = useState(0);

  const loadHistory = () => http.get("/admin/content-studio/history").then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    http.get("/formations").then((r) => {
      setFormations(r.data);
      if (r.data.length) setSlug(r.data[0].slug);
    }).catch(() => {});
    loadHistory();
  }, []);

  const generate = async () => {
    if (!slug) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await http.post("/admin/content-studio/generate", { formation_slug: slug, content_type: type });
      setResult(r.data);
      loadHistory();
      toast.success("Contenu généré");
    } catch {
      toast.error("Erreur", { description: "La génération a échoué, veuillez réessayer." });
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    try {
      await http.delete(`/admin/content-studio/${id}`);
      setHistory((h) => h.filter((x) => x.id !== id));
      if (result?.id === id) setResult(null);
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const genImage = async (id) => {
    setImgLoadingId(id);
    try {
      await http.post(`/admin/content-studio/${id}/image`);
      setImgVersion((v) => v + 1);
      const mark = (x) => (x.id === id ? { ...x, has_image: true } : x);
      setHistory((h) => h.map(mark));
      setResult((r) => (r && r.id === id ? { ...r, has_image: true } : r));
      toast.success("Visuel généré", { description: "Prêt à publier sur Instagram et LinkedIn." });
    } catch {
      toast.error("Erreur", { description: "La génération du visuel a échoué, veuillez réessayer." });
    } finally { setImgLoadingId(null); }
  };

  const visualProps = {
    imgUrl: (id) => `${API}/admin/content-studio/${id}/image?v=${imgVersion}`,
    imgLoadingId,
    onGenImage: genImage,
  };

  return (
    <div data-testid="content-studio">
      <GeneratorForm formations={formations} slug={slug} setSlug={setSlug} type={type} setType={setType} loading={loading} onGenerate={generate} />
      {result && <ResultCard result={result} visualProps={visualProps} />}
      {history.length > 0 && <HistoryList history={history} onRemove={remove} visualProps={visualProps} />}
    </div>
  );
};
